'use client'

import {
  ActionIcon,
  Button,
  Divider,
  Popover,
  PopoverDropdown,
  PopoverTarget,
  UnstyledButton
} from '@mantine/core'
import { useRouter } from 'next/navigation'
import { parseAsInteger, useQueryStates } from 'nuqs'
import { useState, useTransition } from 'react'
import { Icon } from '~/components/icons/icon'

type GuestSelectorProps = {
  showIcon?: boolean
  isRefresh?: boolean
}
export function GuestSelector({
  isRefresh,
  showIcon = true
}: GuestSelectorProps) {
  const router = useRouter()
  const [, startTransition] = useTransition()
  const [isOpen, setIsOpen] = useState(false)

  // URL state
  // TODO: move this to the global filters
  const [guests, setGuests] = useQueryStates({
    adults: parseAsInteger.withDefault(1),
    children: parseAsInteger.withDefault(0),
    guests: parseAsInteger
      .withDefault(1)
      .withOptions({ startTransition, shallow: false })
  })

  // Local state
  const [guest, setGuest] = useState({
    children: guests.children,
    adults: guests.adults
  })

  const total = guest.adults + guest.children || 1

  return (
    <Popover
      shadow="md"
      position="bottom"
      width="250px"
      opened={isOpen}
      onChange={() => setIsOpen((value) => !value)}
    >
      <PopoverTarget>
        <UnstyledButton
          className="inline-flex h-9 w-full flex-1 items-center justify-between whitespace-nowrap rounded-lg px-4 font-semibold text-slate-800 outline-2 outline-transparent hover:cursor-pointer hover:bg-slate-100"
          onClick={() => setIsOpen((value) => !value)}
        >
          <div className="flex w-full justify-between">
            <div className="flex gap-3">
              {showIcon ? <Icon name="users" /> : null}
              <p className="text-sm font-normal">
                {/* {numberOfGuests} {numberOfGuests <= 1 ? 'Guest' : 'Guests'} */}
                {total} {total <= 1 ? 'Guest' : 'Guests'}
              </p>
            </div>
            <Icon name="chevron-down" />
          </div>
        </UnstyledButton>
      </PopoverTarget>
      <PopoverDropdown
        className="z-[100] w-full max-w-full overflow-y-auto md:h-auto"
        classNames={{
          dropdown: 'rounded-xl p-3  shadow-lg '
        }}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-0">
                <p className="text-sm font-bold">Adults</p>
                <span className="text-xs font-normal">Ages 18 & above</span>
              </div>
              <div className="flex items-center gap-2">
                <ActionIcon
                  color="gray"
                  size="sm"
                  radius="xl"
                  variant="outline"
                  disabled={guest.adults === 1}
                  onClick={() => {
                    setGuest((prevState) => ({
                      ...prevState,
                      adults: prevState.adults - 1
                    }))
                  }}
                >
                  <Icon name="minus" />
                </ActionIcon>
                <span className="text-sm">{guest.adults}</span>
                <ActionIcon
                  color="gray"
                  size="sm"
                  radius="xl"
                  variant="outline"
                  onClick={() => {
                    setGuest((prevState) => ({
                      ...prevState,
                      adults: prevState.adults + 1
                    }))
                  }}
                >
                  <Icon name="plus" />
                </ActionIcon>
              </div>
            </div>
            <Divider
              className="border-slate-100"
              styles={{ root: { borderColor: '' } }}
            />
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <p className="text-sm font-bold">Children</p>
                <span className="text-xs">Ages 17 & below</span>
              </div>
              <div className="flex items-center gap-2">
                <ActionIcon
                  color="gray"
                  size="sm"
                  radius="xl"
                  variant="outline"
                  disabled={guest.children === 0}
                  onClick={() => {
                    setGuest((prevState) => ({
                      ...prevState,
                      children: prevState.children - 1
                    }))
                  }}
                >
                  <Icon name="minus" />
                </ActionIcon>
                {/* <p className="mx-2">{guests.children}</p> */}
                <span className="text-sm">{guest.children}</span>
                <ActionIcon
                  color="gray"
                  size="sm"
                  radius="xl"
                  variant="outline"
                  onClick={() => {
                    setGuest((prevState) => ({
                      ...prevState,
                      children: prevState.children + 1
                    }))
                  }}
                >
                  <Icon name="plus" />
                </ActionIcon>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => {
                if (guests.children || guests.adults || guests.guests) {
                  setGuests({
                    adults: guests.adults,
                    children: guests.children,
                    guests: guests.guests
                  })

                  setGuest({
                    children: guests.children,
                    adults: guests.adults
                  })
                }

                if (!guests) {
                  setGuests(() => ({
                    adults: 1,
                    total: 0,
                    children: 0
                  }))

                  setGuest(() => ({
                    adults: 1,
                    total: 0,
                    children: 0
                  }))
                }

                setIsOpen(false)
              }}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              onClick={() => {
                setGuests({
                  children: guest.children,
                  adults: guest.adults,
                  guests: total
                })
                setIsOpen(false)

                if (isRefresh) {
                  startTransition(() => {
                    router.refresh()
                  })
                }
              }}
            >
              Update
            </Button>
          </div>
        </div>
      </PopoverDropdown>
    </Popover>
  )
}
